import { useCallback, useMemo } from 'react';
import { TimeField, Label, FieldError } from '@heroui/react';
import type { TimeValue } from 'react-aria-components';

import {
  FieldValues,
  Path,
  useController,
  UseControllerProps
} from 'react-hook-form';
import { useGlobalComponent } from '@rhino-project/core/hooks';
import { stripRhinoProps } from './utils/stripRhinoProps';
import { parseAbsoluteToLocal } from '@internationalized/date';

export type FieldTimeInputProps<T extends FieldValues = FieldValues> = Omit<
  UseControllerProps<T>,
  'name' | 'control'
> & {
  parse?: (value: string) => TimeValue | null | undefined;
  path: Path<T>;
  label?: string;
  isRequired?: boolean;
  className?: string;
  [key: string]: unknown;
};

export const FieldTimeInputBase = <T extends FieldValues = FieldValues>({
  path,
  parse = parseAbsoluteToLocal,
  label,
  isRequired,
  className,
  ...props
}: FieldTimeInputProps<T>) => {
  const {
    field: { onChange, disabled, value: fieldValue, ...fieldProps },
    fieldState: { error }
  } = useController({ name: path });

  const value = useMemo(
    () => (fieldValue ? parse(fieldValue) : null),
    [fieldValue, parse]
  );

  const handleOnChange = useCallback(
    (value: TimeValue | null) => {
      onChange(
        value
          ? new Date(
              2000,
              1,
              1,
              value.hour,
              value.minute,
              value.second
            ).toISOString()
          : null
      );
    },
    [onChange]
  );

  return (
    <TimeField
      {...fieldProps}
      granularity="second"
      hideTimeZone
      isInvalid={!!error}
      isDisabled={disabled}
      isRequired={isRequired}
      onChange={handleOnChange}
      value={value}
      className={className}
      {...stripRhinoProps(props)}
    >
      {label && <Label>{label}</Label>}
      <TimeField.Group>
        <TimeField.Input>
          {(segment) => <TimeField.Segment segment={segment} />}
        </TimeField.Input>
      </TimeField.Group>
      <FieldError>{error?.message}</FieldError>
    </TimeField>
  );
};

export const FieldTimeInput = (props: FieldTimeInputProps) =>
  useGlobalComponent('FieldTimeInput', FieldTimeInputBase, props);
